// File: src/components/NavBarWithAuth.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import LocationField from "./LocationField";
import theme from "../theme";
import { ReactComponent as TribeLogo } from "../assets/TribeLogo.svg";
import { LuUser } from "react-icons/lu";

const landingNavStyles = `
  /* Apply the overlay appearance only when the guest landing hero exists. */
  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav {
    background: transparent !important;
  }

  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav__line {
    display: none !important;
  }

  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav__logo {
    filter: brightness(0) invert(1);
  }

  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav__brand,
  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav__login {
    color: #fff !important;
  }

  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav__account,
  .guest-page:has(.tribeCarouselHero) .tribe-auth-nav__signup {
    background: rgba(0, 0, 0, 0.28) !important;
    color: #fff !important;
    border: 1px solid rgba(255, 255, 255, 0.6) !important;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .tribe-auth-nav button:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 4px;
  }

  .guest-page:has(.tribeCarouselHero)
  .tribe-auth-nav__logo-button:focus-visible {
    outline-color: #fff;
  }
`;

export default function NavBarWithAuth({
  selectedMarketId,
  onMarketSelected,

  // Compatibility with the existing GuestLayout prop name.
  // Receives { marketId, label, market }, never coordinates.
  onLocationSelected,

  onLogoClick,
}) {
  const navigate = useNavigate();

  const [screenWidth, setScreenWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleMarketPick = (selection) => {
    if (!selection?.marketId) return;

    const callback = onMarketSelected || onLocationSelected;
    callback?.(selection);
  };

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
      return;
    }

    navigate("/");
  };

  const isMobile = screenWidth <= 440;
  const isExtraSmall = screenWidth <= 332;

  const sidePadding =
    screenWidth <= 340
      ? 10
      : screenWidth <= 375
        ? 12
        : theme.spacing.md;

  const rowGap = screenWidth <= 375 ? 6 : 8;

  const navContainerStyle = {
    width: "100%",
    backgroundColor: "#fff",
    fontFamily: theme.typography.fontFamilyBase,
    overflowX: "visible",
  };

  const bottomLineStyle = {
    width: "100%",
    margin: "1px auto 0",
    borderBottom: "1px solid #eee",
  };

  const logoButtonStyle = {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    padding: 0,
    border: "none",
    background: "transparent",
  };

  if (isMobile) {
    const rowStyle = {
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      height: 50,
      padding: `0 ${sidePadding}px`,
      gap: rowGap,
    };

    const logoStyle = {
      width: screenWidth <= 350 ? 66 : 70,
      height: "auto",
      display: "block",
    };

    const locationWrapperStyle = {
      flex: "1 1 0",
      minWidth: 0,
      display: "flex",
      justifyContent: "center",
      overflow: "hidden",
    };

    const accountBtnCommon = {
      boxSizing: "border-box",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      cursor: "pointer",
      fontFamily: theme.typography.fontFamilyBase,
      WebkitTapHighlightColor: "transparent",
      backgroundColor: "#000",
      color: "#fff",
    };

    const accountBtnStyle = isExtraSmall
      ? {
          ...accountBtnCommon,
          borderRadius: "50%",
          width: 36,
          height: 36,
          fontSize: 0,
        }
      : {
          ...accountBtnCommon,
          borderRadius: 100,
          height: 32,
          minWidth: 70,
          padding: "0 12px",
          gap: 4,
          fontSize: "12px",
          fontWeight: 750,
          lineHeight: 1,
          whiteSpace: "nowrap",
        };

    return (
      <div className="tribe-auth-nav" style={navContainerStyle}>
        <style>{landingNavStyles}</style>

        <div style={rowStyle}>
          <button
            type="button"
            className="tribe-auth-nav__logo-button"
            onClick={handleLogoClick}
            style={logoButtonStyle}
            aria-label="Tribe home"
          >
            <TribeLogo
              className="tribe-auth-nav__logo"
              style={logoStyle}
              aria-hidden="true"
            />
          </button>
<div style={{ flex: 1 }} />

          <button
            type="button"
            className="tribe-auth-nav__account"
            style={accountBtnStyle}
            onClick={() => navigate("/login")}
            aria-label="Login"
          >
            <LuUser
              style={{ fontSize: 14, flexShrink: 0 }}
              aria-hidden="true"
            />

            {!isExtraSmall && (
              <span style={{ display: "flex", alignItems: "center" }}>
                Login
              </span>
            )}
          </button>
        </div>

        <div
          className="tribe-auth-nav__line"
          style={bottomLineStyle}
          id="authNavLine"
        />
      </div>
    );
  }

  const navRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 35,
    padding: `0 ${theme.spacing.md}px`,
  };

  const leftBlockStyle = {
    display: "flex",
    alignItems: "center",
  };

  const brandTextStyle = {
    fontFamily: theme.typography.fontFamilyBase,
    fontWeight: 1000,
    fontSize: 25,
    color: theme.colors.textMain,
    flexShrink: 0,
  };

  const locationWrapperStyle = {
    marginLeft: 35,
  };

  const rightBlockStyle = {
    display: "flex",
    alignItems: "center",
    position: "relative",
  };

  const loginButtonStyle = {
    background: "none",
    border: "none",
    color: theme.colors.textMain,
    cursor: "pointer",
    fontSize: ".9rem",
    fontFamily: theme.typography.fontFamilyBase,
    marginRight: 10,
  };

  const signUpButtonStyle = {
    boxSizing: "border-box",
    backgroundColor: "#000",
    color: "#fff",
    borderRadius: 6,
    height: 35,
    border: "none",
    padding: "8px 24px",
    cursor: "pointer",
    marginRight: 10,
    fontSize: ".9rem",
    fontFamily: theme.typography.fontFamilyBase,
  };

  return (
    <div className="tribe-auth-nav" style={navContainerStyle}>
      <style>{landingNavStyles}</style>

      <div style={navRowStyle}>
        <div style={leftBlockStyle}>
          <button
            type="button"
            className="tribe-auth-nav__logo-button"
            onClick={handleLogoClick}
            style={logoButtonStyle}
            aria-label="Tribe home"
          >
            <span
              className="tribe-auth-nav__brand"
              style={brandTextStyle}
            >
              [TRI]BE
            </span>
          </button>

          <div style={locationWrapperStyle}>
            <LocationField
              selectedMarketId={selectedMarketId}
              onMarketSelected={handleMarketPick}
            />
          </div>
        </div>

        <div style={rightBlockStyle}>
          <button
            type="button"
            className="tribe-auth-nav__login"
            style={loginButtonStyle}
            onClick={() => navigate("/login")}
          >
            Login
          </button>

          <button
            type="button"
            className="tribe-auth-nav__signup"
            style={signUpButtonStyle}
            onClick={() => navigate("/register")}
          >
            Sign Up
          </button>
        </div>
      </div>

      <div
        className="tribe-auth-nav__line"
        style={bottomLineStyle}
      />
    </div>
  );
}